import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/lib/i18n';
import { useApi } from '@/Hooks/useApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { EmptyState, LoadingState } from '@/components/ui/states';
import type { LiveTable } from '../lib/live-table';
import {
  BillPane,
  ConsumedProducts,
  Fact,
  PaneTabs,
  ordersTotalOf,
  useVisitChecks,
  type OrderRow,
  type VisitPane,
} from './VisitPanes';

interface SessionDetail {
  id: string;
  status: string;
  guestCount: number;
  openedAt: string;
  waiter?: { id: string; firstName: string; lastName: string | null } | null;
  orders: OrderRow[];
}

export type SessionPane = VisitPane;

interface Props {
  table: LiveTable;
  sessionId: string | null;
  open: boolean;
  /** Which half the menu asked for; the other is one click away. */
  initialPane?: SessionPane;
  onClose: () => void;
  onChanged: () => void;
}

/**
 * What is happening at this table right now.
 *
 * This is the detail the live panel used to show below the map. It reads better
 * on the table itself: the operator has already pointed at the table, and the
 * panel made them look somewhere else to find out what they had pointed at.
 *
 * Both halves — consumed products and the bill — live in `VisitPanes`, which is
 * where they stay: they are the two questions a visit answers, and the dialog is
 * only the frame this one is opened from.
 */
export function TableSessionDialog({
  table,
  sessionId,
  open,
  initialPane = 'products',
  onClose,
  onChanged,
}: Props) {
  const { t, i18n } = useTranslation();
  const { format, decimals } = useCurrency(table.branchId);
  const [pane, setPane] = useState<SessionPane>(initialPane);

  useEffect(() => setPane(initialPane), [initialPane, sessionId]);

  const detail = useApi<SessionDetail>(open && sessionId ? `/sessions/${sessionId}` : null);
  const session = detail.data;

  const bill = useVisitChecks(open && sessionId ? { sessionId } : null, table.branchId);

  const afterChange = useCallback(() => {
    bill.reload();
    detail.reload();
    onChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill.reload, detail.reload, onChanged]);

  const ordersTotal = ordersTotalOf(session?.orders, decimals);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('spaces.session.title', { number: table.number })}
      description={
        bill.outstanding
          ? `${table.areaName} · ${t('spaces.checks.outstanding', { amount: format(bill.outstanding) })}`
          : table.areaName
      }
      // Near full-bleed: this is the screen a manager works a table from, and a
      // narrow modal made the bill and the item list both cramped.
      className="w-full max-w-[1400px]"
    >
      {!sessionId ? (
        <EmptyState title={t('spaces.session.none')} hint={t('spaces.session.noneHint')} />
      ) : detail.loading && !session ? (
        <LoadingState label={t('common.loading')} />
      ) : !session ? (
        <EmptyState title={t('spaces.session.none')} hint={t('spaces.session.noneHint')} />
      ) : (
        <div className="space-y-4">
          {/* One dialog, two halves: what they consumed, and what they owe.
              They were separate modals and the operator had to close one to
              reach the other, for the same table they had already chosen. */}
          <PaneTabs pane={pane} onPick={setPane} />

          <div className={pane === 'products' ? 'space-y-4' : 'hidden'}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Fact
                label={t('spaces.live.state')}
                value={
                  <Badge tone={toneForStatus(table.state)}>
                    {t(`table.state.${table.state}`, table.state)}
                  </Badge>
                }
              />
              <Fact
                label={t('spaces.session.openedAt')}
                value={formatDateTime(session.openedAt, i18n.language)}
              />
              {/* 1 is the server's default for a session opened without a head
                  count, so printing it would report a number nobody entered. */}
              {session.guestCount > 1 && (
                <Fact label={t('spaces.live.guests')} value={String(session.guestCount)} />
              )}
              {session.waiter && (
                <Fact
                  label={t('spaces.session.waiter')}
                  value={`${session.waiter.firstName} ${session.waiter.lastName ?? ''}`.trim()}
                />
              )}
              {ordersTotal && (
                <Fact label={t('spaces.session.ordersTotal')} value={format(ordersTotal)} />
              )}
            </div>

            <ConsumedProducts
              orders={session.orders ?? []}
              branchId={table.branchId}
              onChanged={afterChange}
            />
          </div>

          <div className={pane === 'bill' ? 'space-y-4' : 'hidden'}>
            <BillPane bill={bill} branchId={table.branchId} onChanged={afterChange} />
          </div>
        </div>
      )}
    </Dialog>
  );
}
